import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState
} from 'react'

import { fetchAll } from '@/services/delivery/fetchAll'
import { BaseFilters, useCommonContext } from '@/hooks/use-common-context'

interface IDeliveryProps {
  activeDrawer: boolean
  currentFilters: BaseFilters | undefined
  delivery: any
  deliveryRefetchTrigger: number
  handleFilter: (filtros: BaseFilters) => Promise<void>
  handleLimitChange: (newLimit: number) => void
  handleSelectItem: (item: string[], action: boolean) => void
  isLoading: boolean
  limit: number
  page: number
  results: number
  search: string
  selectItems: any[]
  setActiveDrawer: React.Dispatch<React.SetStateAction<boolean>>
  setCurrentFilters: React.Dispatch<React.SetStateAction<BaseFilters | undefined>>
  setDelivery: React.Dispatch<React.SetStateAction<any>>
  setLimit: React.Dispatch<React.SetStateAction<number>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setResults: React.Dispatch<React.SetStateAction<number>>
  setSearch: React.Dispatch<React.SetStateAction<string>>
  setSelectItems: React.Dispatch<React.SetStateAction<any[]>>
  setTableData: React.Dispatch<React.SetStateAction<any[]>>
  tableData: any[]
  triggerDeliveryRefetch: () => void
}

const DeliveryContext = createContext<IDeliveryProps | undefined>(undefined)

const DeliveryProvider = ({ children }: { children: ReactNode }) => {
  const [delivery, setDelivery] = useState<any>(null)
  const [deliveryRefetchTrigger, setDeliveryRefetchTrigger] = useState(0)

  const triggerDeliveryRefetch = () => setDeliveryRefetchTrigger(prev => prev + 1)

  const {
    activeDrawer, currentFilters, handleFilter, handleLimitChange,
    handleSelectItem, isLoading, limit, page, results, search, selectItems,
    setActiveDrawer, setCurrentFilters, setLimit, setPage, setResults,
    setSearch, setSelectItems, setTableData, tableData,
  } = useCommonContext(fetchAll, { hasAgrupamento: true, adjustEndDate: true, afterFilter: triggerDeliveryRefetch })

  const contextValue = useMemo(
    () => ({
      activeDrawer,
      currentFilters,
      delivery,
      deliveryRefetchTrigger,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      page,
      results,
      search,
      selectItems,
      setActiveDrawer,
      setCurrentFilters,
      setDelivery,
      setLimit,
      setPage,
      setResults,
      setSearch,
      setSelectItems,
      setTableData,
      tableData,
      triggerDeliveryRefetch,
    }),
    [
      activeDrawer,
      currentFilters,
      delivery,
      deliveryRefetchTrigger,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      page,
      results,
      search,
      selectItems,
      tableData,
    ]
  )

  return (
    <DeliveryContext.Provider value={contextValue}>
      {children}
    </DeliveryContext.Provider>
  )
}

const useDeliveryContext = () => {
  const context = useContext(DeliveryContext)
  if (context === undefined) {
    throw new Error('useDeliveryContext must be used within a DeliveryProvider')
  }
  return context
}

export { DeliveryProvider, useDeliveryContext }
